import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Search } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { ARTICLES } from "@/constants/articles";
import { usePageMeta } from "@/hooks/usePageMeta";

const Insights = () => {
  usePageMeta(
    "Radiation Shielding Design Resources | Heartland Physics",
    "Practical guidance on shielding design, plan review, construction, and compliance for X-ray, CT, dental, veterinary, mammography, and imaging projects."
  );
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return ARTICLES;
    return ARTICLES.filter((a) =>
      `${a.title} ${a.excerpt} ${a.tag}`.toLowerCase().includes(needle)
    );
  }, [q]);

  const topical = filtered.filter((a) => a.tag !== "Registration Guide");
  const guides = filtered.filter((a) => a.tag === "Registration Guide");

  return (
    <>
      <section data-testid="insights-hero" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
          <Reveal y={12}>
            <p className="eyebrow">Insights — The Heartland Physics journal</p>
          </Reveal>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines delay={0.1} lines={["Practical guidance."]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="insights-intro">
              Plain-language resources on shielding design, plan review, construction, and compliance — written for facility owners, architects, and contractors.
            </p>
          </Reveal>
          <Reveal delay={0.65} className="mt-9">
            <div className="relative max-w-md">
              <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles — try your state, CT, dental…"
                data-testid="insights-search-input"
                className="w-full border border-border bg-white py-3.5 pl-11 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-blueprint"
              />
            </div>
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-ink/45" data-testid="insights-count">
              {filtered.length} article{filtered.length === 1 ? "" : "s"}
            </p>
            <Link to="/shielding-requirements" data-testid="insights-states-link" className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blueprint transition-colors hover:text-ink">
              Browse requirements by state <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {topical.length > 0 && (
        <section className="border-b border-border bg-white">
          <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
            <Reveal>
              <p className="eyebrow">Shielding design & practice</p>
            </Reveal>
            <div className="mt-8 border-t border-border">
              {topical.map((a, i) => (
                <Reveal key={a.slug} delay={Math.min(i * 0.05, 0.3)}>
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`insights-article-${a.slug}`}
                    className="group grid gap-4 border-b border-border py-8 transition-colors duration-200 hover:bg-paper md:grid-cols-[80px_1fr_auto] md:items-center md:gap-8 md:px-4"
                  >
                    <span className="font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</span>
                    <span>
                      <span className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">{a.tag}</span>
                      </span>
                      <span className="mt-2 block font-heading text-lg font-bold tracking-tight text-ink transition-colors duration-200 group-hover:text-blueprint md:text-xl">
                        {a.title}
                      </span>
                      <span className="mt-2 block max-w-2xl text-sm leading-relaxed text-ink/60">{a.excerpt}</span>
                    </span>
                    <ArrowUpRight size={20} className="hidden text-ink/30 transition-all duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-blueprint md:block" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {guides.length > 0 && (
        <section className="border-b border-border bg-blueprint-grid">
          <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
            <Reveal className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="eyebrow">Registration guides</p>
                <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink md:text-xl">
                  How to register an X-ray machine — every state & territory
                </h2>
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-ink/45">{guides.length} guides</p>
            </Reveal>
            <div className="mt-10 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
              {guides.map((a, i) => (
                <Reveal key={a.slug} delay={Math.min(i * 0.03, 0.4)} className="h-full">
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`insights-guide-${a.slug}`}
                    className="group flex h-full flex-col bg-white p-6 transition-colors duration-300 hover:bg-ink"
                  >
                    <span className="font-heading text-sm font-bold leading-snug tracking-tight text-ink transition-colors duration-300 group-hover:text-white">
                      {a.title}
                    </span>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-xs font-semibold text-blueprint transition-colors duration-300 group-hover:text-azure">
                      Read the guide <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-5 border border-blueprint/30 bg-blueprint/5 p-7 md:flex-row md:items-center" data-testid="insights-cta">
              <p className="text-sm leading-relaxed text-ink/75">
                <span className="font-semibold text-ink">Working on a project now?</span> Skip the reading — send the floor plan and hear back within one business day.
              </p>
              <Link
                to="/request"
                data-testid="insights-cta-request"
                className="group flex shrink-0 items-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
              >
                Request a Shielding Design
                <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Insights;
